import type { DockmasterSpan } from "../../schemas/codegen";
import type {
  EnhancedTrace,
  TraceTransportType,
} from "../components/CodeGen/Chat/components/traces/types";

/**
 * UI hierarchy type
 */
export interface HierarchicalSpan extends DockmasterSpan {
  children: HierarchicalSpan[];
}

/**
 * Trace metadata
 */
export interface TraceMetadata {
  traceId: string;
  startTime: number;
  endTime: number;
  duration: number;
  spanCount: number;
  hasErrors: boolean;
}

/**
 * Check if a span is a root span (has no parent)
 */
export const isRootSpan = (span: DockmasterSpan): boolean => !span.parentSpanId;

/**
 * Check if any spans have errors - including HTTP transport errors,
 * JSON-RPC protocol errors, and MCP tool execution errors
 */
export const hasErrors = (spans: DockmasterSpan[]): boolean =>
  spans.some((span) => {
    // 1. Transport-level errors (HTTP status)
    if (span.status === "error") {
      return true;
    }

    // 2. JSON-RPC protocol errors
    if (span.attributes?.["mcp.error.code"]) {
      return true;
    }

    // 3. MCP execution errors (tool/prompt/resource failures)
    if (
      span.attributes?.["mcp.tool.result.is_error"] === true ||
      span.attributes?.["mcp.prompt.result.is_error"] === true ||
      span.attributes?.["mcp.resource.result.is_error"] === true
    ) {
      return true;
    }

    return false;
  });

/**
 * Extract MCP tool name from span operation details (deprecated - use getMcpToolNameFromAttributes)
 */
export const getMcpToolName = (span: DockmasterSpan): string | null => {
  return (span.attributes?.["mcp.tool.name"] as string | null) || null;
};

export const getMcpResourceName = (span: DockmasterSpan): string | null => {
  return (span.attributes?.["mcp.resource.uri"] as string | null) || null;
};

/**
 * Extract MCP method name from span operation details (deprecated - use getMcpMethodNameFromAttributes)
 */
export const getMcpMethodName = (span: DockmasterSpan): string | null => {
  return (span.attributes?.["mcp.method.name"] as string | null) || null;
};

/**
 * Parse operation details, handling both string and object formats (deprecated - use attributes directly)
 */
export const parseOperationDetails = (
  span: DockmasterSpan,
): Record<string, unknown> | null => {
  // Return attributes directly since operationDetails is no longer used
  return span.attributes as Record<string, unknown> | null;
};

/**
 * Parse tool calls, handling both string and object formats (deprecated - toolCalls no longer exists)
 */
export const parseToolCalls = (_span: DockmasterSpan): unknown | null => {
  // toolCalls property no longer exists in the new schema
  return null;
};

/**
 * Calculate the depth of a span in the hierarchy
 */
export const getSpanDepth = (
  span: DockmasterSpan,
  spans: DockmasterSpan[],
): number => {
  if (!span.parentSpanId) {
    return 0;
  }

  const parent = spans.find((s) => s.spanId === span.parentSpanId);
  if (!parent) {
    return 0;
  }

  return getSpanDepth(parent, spans) + 1;
};

/**
 * Build hierarchical span structure from flat spans
 */
export const buildSpanHierarchy = (
  spans: DockmasterSpan[],
): HierarchicalSpan[] => {
  const spanMap = new Map<string, HierarchicalSpan>();

  // Create map of all spans with empty children arrays
  for (const span of spans) {
    spanMap.set(span.spanId, { ...span, children: [] });
  }

  // Build hierarchy by connecting parents and children
  const rootSpans: HierarchicalSpan[] = [];

  for (const span of spans) {
    const hierarchicalSpan = spanMap.get(span.spanId);
    if (!hierarchicalSpan) {
      continue;
    }

    if (span.parentSpanId) {
      const parent = spanMap.get(span.parentSpanId);
      if (parent) {
        parent.children.push(hierarchicalSpan);
      } else {
        // Parent not found, treat as root
        rootSpans.push(hierarchicalSpan);
      }
    } else {
      rootSpans.push(hierarchicalSpan);
    }
  }

  // Sort children by start time
  const sortChildren = (spans: HierarchicalSpan[]) => {
    spans.sort(
      (a, b) =>
        (a as DockmasterSpan).startTime - (b as DockmasterSpan).startTime,
    );
    for (const span of spans) {
      sortChildren(span.children);
    }
  };

  sortChildren(rootSpans);
  return rootSpans;
};

/**
 * Calculate trace metadata from spans
 */
export const getTraceMetadata = (spans: DockmasterSpan[]): TraceMetadata => {
  if (spans.length === 0) {
    throw new Error("Cannot calculate metadata for empty spans array");
  }

  const firstSpanTraceId = spans[0].traceId;
  const startTime = Math.min(...spans.map((s) => s.startTime));
  const endTime = Math.max(...spans.map((s) => s.endTime || s.startTime));
  const duration = endTime - startTime;
  const spanCount = spans.length;
  const traceHasErrors = hasErrors(spans);

  return {
    traceId: firstSpanTraceId,
    startTime,
    endTime,
    duration,
    spanCount,
    hasErrors: traceHasErrors,
  };
};

/**
 * Group spans by trace ID and return enhanced traces
 */
export const groupSpansByTrace = (spans: DockmasterSpan[]) => {
  const traceMap = new Map<string, DockmasterSpan[]>();

  // Group spans by traceId
  for (const span of spans) {
    const existing = traceMap.get(span.traceId) || [];
    existing.push(span);
    traceMap.set(span.traceId, existing);
  }

  // Convert to enhanced traces
  return Array.from(traceMap.entries())
    .map(([, traceSpans]) => {
      if (traceSpans.length === 0) {
        return null;
      }

      const metadata = getTraceMetadata(traceSpans);
      const enhancedSpans = createEnhancedSpans(traceSpans);

      return {
        ...metadata,
        spans: enhancedSpans,
      };
    })
    .filter((trace): trace is NonNullable<typeof trace> => trace !== null)
    .sort((a, b) => b.startTime - a.startTime);
};

// Import the EnhancedSpan type from the component types file
import type { EnhancedSpan } from "../components/CodeGen/Chat/components/traces/types";

/**
 * Create enhanced spans with computed properties
 */
function createEnhancedSpans(spans: DockmasterSpan[]): EnhancedSpan[] {
  const buildEnhancedTree = (
    spans: DockmasterSpan[],
    parentId: string | null,
    depth: number,
  ): EnhancedSpan[] => {
    const children: EnhancedSpan[] = spans
      .filter((s) => s.parentSpanId === parentId)
      .sort((a, b) => a.startTime - b.startTime)
      .map((span): EnhancedSpan => {
        const parsedOperationDetails = parseOperationDetails(span);
        const parsedToolCalls = parseToolCalls(span);
        const childSpans = buildEnhancedTree(spans, span.spanId, depth + 1);

        return {
          ...span,
          isRootSpan: parentId === null,
          depth,
          mcpToolName: getMcpToolName(span),
          mcpMethodName: getMcpMethodName(span),
          parsedToolCalls,
          parsedOperationDetails,
          ...(childSpans.length > 0 ? { children: childSpans } : {}),
        };
      });

    return children;
  };

  return buildEnhancedTree(spans, null, 0);
}

/**
 * Detect transport type from trace spans
 */
export const getTraceTransportType = (
  trace: EnhancedTrace,
): TraceTransportType => {
  const rootSpan = trace.spans?.find((span) => span.isRootSpan);
  if (!rootSpan) {
    return "generic";
  }

  // Check for MCP transport indicators
  if (
    rootSpan.attributes?.["mcp.method.name"] ||
    rootSpan.attributes?.["mcp.transport"] ||
    rootSpan.attributes?.["mcp.tool.name"]
  ) {
    return "http-rpc";
  }

  // Check for SSE transport indicators
  if (
    rootSpan.attributes?.["sse.connection.id"] ||
    rootSpan.attributes?.content_type === "text/event-stream" ||
    rootSpan.operationName?.includes("sse")
  ) {
    return "sse";
  }

  return "generic";
};

/**
 * Extract app name from span attributes
 */
export const getAppName = (span: DockmasterSpan): string | null => {
  return (span.attributes?.["app.name"] as string | null) || null;
};

/**
 * Extract HTTP status code from span attributes
 */
export const getHttpStatusCode = (span: DockmasterSpan): number | null => {
  const status = span.attributes?.["http.status_code"];
  return typeof status === "number" ? status : null;
};

/**
 * Extract MCP tool name from attributes (updated to use attributes)
 */
export const getMcpToolNameFromAttributes = (
  span: DockmasterSpan,
): string | null => {
  return (span.attributes?.["mcp.tool.name"] as string | null) || null;
};

/**
 * Extract MCP method name from attributes (updated to use attributes)
 */
export const getMcpMethodNameFromAttributes = (
  span: DockmasterSpan,
): string | null => {
  return (span.attributes?.["mcp.method.name"] as string | null) || null;
};

/**
 * Extract MCP session ID from attributes
 */
export const getMcpSessionId = (span: DockmasterSpan): string | null => {
  return (span.attributes?.["mcp.session.id"] as string | null) || null;
};

/**
 * Extract MCP request ID from attributes
 */
export const getMcpRequestId = (span: DockmasterSpan): string | null => {
  return (span.attributes?.["mcp.request.id"] as string | null) || null;
};

/**
 * Extract MCP request arguments from attributes
 */
export const getMcpRequestArguments = (
  span: DockmasterSpan,
): Record<string, unknown> => {
  const args: Record<string, unknown> = {};
  if (!span.attributes) {
    return args;
  }

  // Find all attributes that start with 'mcp.request.argument.'
  for (const [key, value] of Object.entries(span.attributes)) {
    if (key.startsWith("mcp.request.argument.")) {
      const argName = key.replace("mcp.request.argument.", "");
      args[argName] = value;
    }
  }

  return args;
};

/**
 * Format duration for different transport types
 */
export const formatDurationForTransport = (
  duration: number,
  transportType: TraceTransportType,
): string => {
  if (transportType === "sse" && duration > 60000) {
    // For SSE, show longer durations in minutes/hours if over 1 minute
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  // For MCP and short durations, show in milliseconds/seconds
  if (duration >= 1000) {
    return `${(duration / 1000).toFixed(1)}s`;
  }
  return `${Math.round(duration)}ms`;
};

/**
 * Snap any string to a valid TraceTransportType and log unsupported values
 */
export const snapToTraceTransportType = (
  transportType: string,
): TraceTransportType => {
  const validTypes: TraceTransportType[] = ["http-rpc", "sse", "generic"];

  if (validTypes.includes(transportType as TraceTransportType)) {
    return transportType as TraceTransportType;
  }

  // Log unsupported transport type
  console.warn(
    `Unsupported transport type encountered: "${transportType}". Defaulting to "generic".`,
  );

  return "generic";
};
